'use client';

import {
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useConvexAuth, useQuery } from 'convex/react';
import { HydrationBoundary } from '@tanstack/react-query';
import { ConvexQueryCacheProvider } from 'convex-helpers/react/cache';
import { useParams, usePathname } from 'next/navigation';
import { useRouter } from 'nextjs-toploader/app';
import { DashboardPageLoader } from '@/components/loaders';
import { Button } from '@/components/ui/button';
import { useLocale } from 'next-intl';
import { AUTH_VIEWER_SYNC_GRACE_MS } from '@/lib/auth';
import { getProtectedWorkspaceCopy } from '@/i18n/protected-workspace-copy';
import { getLoaderCopy } from '@/i18n/loader-copy';
import { api } from '@/convex/_generated/api';
import { WorkspaceAdmissionShell } from '@/components/workspace-admission-shell';
import { useIsRenderIdentity } from './render-identity';
import { QueryHydrationSnapshotProvider } from '@/lib/server-seed/query-hydration-snapshot';
import {
  resolveWorkspaceAdmissionShellVariant,
  type WorkspaceAdmissionShellVariant,
} from '@/components/workspace-admission-shell-variant';
import {
  resolveWorkspaceAdmission,
  type WorkspaceServerAdmission,
  type WorkspaceServerSnapshot,
} from './workspace-admission-state';

export const ConfirmedConvexIdentityContext = createContext<string | null>(
  null
);
const ActiveWorkspaceContext = createContext<{
  userId: string;
  orgSlug: string;
} | null>(null);

/**
 * Convex's `<AuthLoading>`/`<Authenticated>` read only the socket's auth
 * state, which is always loading during server rendering, so pages gated by
 * them could only ever server-render their loading fallback. Inside an
 * admitted workspace (server-verified or client-confirmed) the page is
 * authenticated; logged-out visitors never get past this boundary.
 */
export function WorkspaceAuthGate({
  children,
  loading,
  unauthenticated,
}: {
  children: ReactNode;
  loading: ReactNode;
  unauthenticated: ReactNode;
}) {
  const { isAuthenticated, isLoading } = useWorkspaceRouteAuth();
  if (isAuthenticated) return children;
  return isLoading ? loading : unauthenticated;
}

/** `useConvexAuth()` that counts workspace admission as authenticated. */
export function useWorkspaceRouteAuth(): {
  isAuthenticated: boolean;
  isLoading: boolean;
} {
  const workspace = useContext(ActiveWorkspaceContext);
  const auth = useConvexAuth();
  return workspace
    ? { isAuthenticated: true, isLoading: false }
    : { isAuthenticated: auth.isAuthenticated, isLoading: auth.isLoading };
}

export function useProtectedWorkspace(orgSlug: string) {
  const identity = useContext(ConfirmedConvexIdentityContext);
  const workspace = useContext(ActiveWorkspaceContext);
  const { isAuthenticated, isLoading } = useConvexAuth();
  return !isLoading &&
    isAuthenticated &&
    identity !== null &&
    workspace?.userId === identity &&
    workspace.orgSlug === orgSlug
    ? workspace
    : null;
}

function WorkspaceConnectionPending({
  variant,
}: {
  variant: WorkspaceAdmissionShellVariant;
}) {
  const locale = useLocale();
  const copy = getProtectedWorkspaceCopy(locale);
  // The layout shell is aria-hidden, so its pending state is announced here
  // (the plain loader already carries its own visible text).
  const pendingAnnouncement =
    variant === 'generic' ? '' : getLoaderCopy(locale).shapingWorkspace;
  const [timedOut, setTimedOut] = useState(false);
  const retryRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const timer = window.setTimeout(
      () => setTimedOut(true),
      AUTH_VIEWER_SYNC_GRACE_MS
    );
    return () => window.clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (timedOut) retryRef.current?.focus();
  }, [timedOut]);

  return (
    <>
      <span
        id="workspace-connection-recovery-status"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {timedOut ? copy.recovery : pendingAnnouncement}
      </span>
      {!timedOut ? (
        variant === 'generic' ? (
          <DashboardPageLoader />
        ) : (
          <WorkspaceAdmissionShell variant={variant} />
        )
      ) : (
        <div className="bg-background flex min-h-dvh w-full items-center justify-center px-6">
          <div className="flex flex-col items-center gap-3 text-center">
            <p
              id="workspace-connection-recovery"
              className="text-muted-foreground w-[calc(100vw-3rem)] max-w-md text-sm text-balance break-words"
            >
              {copy.recovery}
            </p>
            <Button
              ref={retryRef}
              type="button"
              variant="outline"
              aria-describedby="workspace-connection-recovery"
              onClick={() => window.location.reload()}
            >
              {copy.retry}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

function WorkspaceMembershipBoundary({
  orgSlug,
  shellVariant,
  serverAdmission,
  children,
}: {
  orgSlug?: string;
  shellVariant: WorkspaceAdmissionShellVariant;
  serverAdmission: WorkspaceServerAdmission | null;
  children: ReactNode;
}) {
  const identity = useContext(ConfirmedConvexIdentityContext);
  const { isAuthenticated, isLoading } = useConvexAuth();
  const canQuery = !isLoading && isAuthenticated && identity !== null;
  const organization = useQuery(
    api.organizations.queries.getBySlug,
    canQuery && orgSlug ? { slug: orgSlug } : 'skip'
  );
  const router = useRouter();
  useEffect(() => {
    if (canQuery && organization === null) router.replace('/dashboard');
  }, [canQuery, organization, router]);
  const admission = resolveWorkspaceAdmission({
    orgSlug,
    identity,
    authLoading: isLoading,
    authenticated: isAuthenticated,
    organization,
    serverAdmission,
  });
  // Pending admission renders only a presentational shell: never the
  // protected children, their providers or subscriptions. A server admission
  // (same token, same workspace) renders the real page, whose subscriptions
  // still wait for client confirmation via useProtectedWorkspace.
  if (!admission.admitted || !orgSlug)
    return <WorkspaceConnectionPending variant={shellVariant} />;
  return (
    <AdmittedWorkspaceProviders userId={admission.userId} orgSlug={orgSlug}>
      {children}
    </AdmittedWorkspaceProviders>
  );
}

/**
 * The organization row above re-delivers on every credit debit, so a running
 * agent turn re-renders this boundary several times a minute. Memoized on
 * primitives (with the parent's stable `children`), the providers below — and
 * every cached query consumer under them — skip those renders. Their context
 * values used to be fresh objects per render, which re-rendered the whole
 * workspace (sidebar, thread page, panels) on each debit.
 */
const AdmittedWorkspaceProviders = memo(function AdmittedWorkspaceProviders({
  userId,
  orgSlug,
  children,
}: {
  userId: string;
  orgSlug: string;
  children: ReactNode;
}) {
  const activeWorkspace = useMemo(
    () => ({ userId, orgSlug }),
    [userId, orgSlug]
  );
  return (
    <ActiveWorkspaceContext.Provider value={activeWorkspace}>
      {/* Protected subscriptions must end immediately when this identity or
          membership boundary unmounts, rather than remaining idle for 5 minutes. */}
      <ConvexQueryCacheProvider maxIdleEntries={0}>
        {children}
      </ConvexQueryCacheProvider>
    </ActiveWorkspaceContext.Provider>
  );
});

export function ProtectedWorkspaceBoundary({
  children,
  serverSnapshot = null,
}: {
  children: ReactNode;
  /** Chrome data and membership the server resolved with this request's token. */
  serverSnapshot?: WorkspaceServerSnapshot | null;
}) {
  const params = useParams<{ orgSlug?: string }>();
  const pathname = usePathname();
  const { isAuthenticated, isLoading } = useConvexAuth();
  const router = useRouter();
  // The dynamic root also serves CMS marketing pages. Only nested workspace
  // routes own protected subscriptions; public/share/auth routes live elsewhere.
  const protectedRoute = (pathname?.split('/').filter(Boolean).length ?? 0) > 1;
  useEffect(() => {
    if (protectedRoute && !isLoading && !isAuthenticated)
      router.replace('/login');
  }, [isAuthenticated, isLoading, protectedRoute, router]);
  // A snapshot read for another account (the tab's account changed since)
  // neither admits nor hydrates; the wrappers stay put so nothing remounts.
  const usable = useIsRenderIdentity(serverSnapshot?.admission.userId ?? null);
  if (!protectedRoute) return children;
  const boundary = (
    <WorkspaceMembershipBoundary
      orgSlug={params.orgSlug}
      shellVariant={resolveWorkspaceAdmissionShellVariant(pathname)}
      serverAdmission={usable ? (serverSnapshot?.admission ?? null) : null}
    >
      {children}
    </WorkspaceMembershipBoundary>
  );
  return serverSnapshot ? (
    <HydrationBoundary state={usable ? serverSnapshot.queryState : undefined}>
      <QueryHydrationSnapshotProvider
        state={usable ? serverSnapshot.queryState : null}
      >
        {boundary}
      </QueryHydrationSnapshotProvider>
    </HydrationBoundary>
  ) : (
    boundary
  );
}
